/**
 * @fileoverview DryRunSummaryStep — the new step-5 summary screen.
 * @module import-editor/dryRunSummary
 *
 * Responsibilities:
 * - Render the four category groups (Imported / Excluded / Failed / Removed)
 *   as accordions, with drill-in RowLevelExplorer tables inside each.
 * - Expose a single commit affordance via props so the container wizard
 *   stays responsible for the confirm flow.
 *
 * Dependencies:
 * - deriveCategories + groupCategoriesByKind + groupCount (pure)
 * - CategoryGroup, CategoryAccordion, RowLevelExplorer (presentation)
 *
 * Architecture:
 * - [Local-first documented bypass] Children fetch via props.fetchRows,
 *   which bypasses Dexie for row data (documented in plan §2).
 *
 * Notes:
 * - This component is used for BOTH the dry-run summary (step 5) AND the
 *   committed-run summary (step 6). The only difference is the category
 *   pill label ("will be created" vs "were created"); the parent controls
 *   that via the `mode` prop.
 */

/**
 * @component DryRunSummaryStep
 *
 * Purpose:
 * - Replace the old flat 5-counter summary with a drill-in taxonomy view.
 *
 * Props:
 * - summary:       DryRunSummaryView — the run's summary
 * - runId:         string — lets children build /rows requests
 * - mode:          'dryRun' | 'commit' — tense hint for strings
 * - fetchRows:     delegate for fetching /rows pages
 * - onExportCsv:   optional — delegate for streaming /rows.csv downloads
 *
 * Behavior:
 * - Derives category descriptors once per summary reference.
 * - Passes through to CategoryGroup → CategoryAccordion → RowLevelExplorer.
 *
 * Data Source:
 * - local (derivation) + remote (row details via fetchRows)
 *
 * Constraints:
 * - MUST NOT render network calls at mount. Accordions start collapsed;
 *   rows fetch only after expansion.
 */

import { useMemo } from 'react';
import {
  deriveCategories,
  groupCategoriesByKind,
  groupCount,
} from './deriveCategories';
import type { DryRunSummaryView } from './types';
import { CategoryGroup } from './CategoryGroup';
import { CategoryAccordion } from './CategoryAccordion';
import { RowLevelExplorer, type FetchRowsParams, type RowsPageResponseView } from './RowLevelExplorer';
import { EnrichmentPreviewPanel, type EnrichmentPreviewRow } from './EnrichmentPreviewPanel';

export interface DryRunSummaryStepProps {
  summary: DryRunSummaryView;
  runId: string;
  mode?: 'dryRun' | 'commit';
  fetchRows: (params: FetchRowsParams) => Promise<RowsPageResponseView>;
  onExportCsv?: (params: { runId: string; category: string; q: string }) => void;
  /** Phase 5.5: Preview rows with enrichment data for owner parsing preview. */
  previewRows?: EnrichmentPreviewRow[];
}

export function DryRunSummaryStep(props: DryRunSummaryStepProps): JSX.Element {
  const { summary, runId, fetchRows, onExportCsv, mode = 'dryRun', previewRows } = props;
  const enrichmentCounts = summary.enrichmentCounts;

  const grouped = useMemo(() => {
    const categories = deriveCategories(summary);
    return groupCategoriesByKind(categories);
  }, [summary]);

  const totalImported = groupCount(grouped.imported);
  const totalExcluded = groupCount(grouped.excluded);
  const totalFailed = groupCount(grouped.failed);
  const totalRemoved = groupCount(grouped.removed);

  const importedLabel = mode === 'commit' ? 'Imported' : 'Will be imported';
  const excludedLabel = mode === 'commit' ? 'Excluded' : 'Will be excluded';
  const failedLabel   = mode === 'commit' ? 'Failed'   : 'Will fail';
  const removedLabel  = mode === 'commit' ? 'Soft-deleted' : 'Will be soft-deleted';

  return (
    <div className="space-y-6" aria-label="Dry run summary">
      {/* Headline totals — one compact row so the user sees 100k / 83k / 12 at a glance */}
      <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1 px-1 text-sm">
        <div className="text-gray-900">
          <span className="font-semibold tabular-nums">{summary.totalRows.toLocaleString()}</span>
          <span className="text-gray-500"> total rows</span>
        </div>
        <HeadlineStat label={importedLabel}  value={totalImported} tone="emerald" />
        <HeadlineStat label={excludedLabel}  value={totalExcluded} tone="amber" />
        {totalFailed > 0  && <HeadlineStat label={failedLabel}  value={totalFailed}  tone="red" />}
        {totalRemoved > 0 && <HeadlineStat label={removedLabel} value={totalRemoved} tone="orange" />}
      </div>

      {/* Phase 5.5: Owner Parsing & Enrichment Preview (dry-run only) */}
      {mode === 'dryRun' && previewRows && previewRows.length > 0 && (
        <EnrichmentPreviewPanel
          previewRows={previewRows}
          enrichmentCounts={enrichmentCounts}
        />
      )}

      <CategoryGroup kind="imported" label={importedLabel} count={totalImported}>
        {grouped.imported.map((d) => (
          <CategoryAccordion key={d.key} descriptor={d}>
            <RowLevelExplorer
              runId={runId}
              category={d.key}
              fetchRows={fetchRows}
              onExportCsv={onExportCsv}
            />
          </CategoryAccordion>
        ))}
      </CategoryGroup>

      <CategoryGroup kind="excluded" label={excludedLabel} count={totalExcluded}>
        {grouped.excluded.map((d) => (
          <CategoryAccordion key={d.key} descriptor={d}>
            <RowLevelExplorer
              runId={runId}
              category={d.key}
              fetchRows={fetchRows}
              onExportCsv={onExportCsv}
            />
          </CategoryAccordion>
        ))}
      </CategoryGroup>

      {totalFailed > 0 && (
        <CategoryGroup kind="failed" label={failedLabel} count={totalFailed}>
          {grouped.failed.map((d) => (
            <CategoryAccordion key={d.key} descriptor={d} defaultOpen={d.count > 0 && totalFailed <= 10}>
              <RowLevelExplorer
                runId={runId}
                category={d.key}
                fetchRows={fetchRows}
                onExportCsv={onExportCsv}
              />
            </CategoryAccordion>
          ))}
        </CategoryGroup>
      )}

      {totalRemoved > 0 && (
        <CategoryGroup kind="removed" label={removedLabel} count={totalRemoved}>
          {grouped.removed.map((d) => (
            <CategoryAccordion key={d.key} descriptor={d}>
              <RowLevelExplorer
                runId={runId}
                category={d.key}
                fetchRows={fetchRows}
                onExportCsv={onExportCsv}
              />
            </CategoryAccordion>
          ))}
        </CategoryGroup>
      )}
    </div>
  );
}

interface HeadlineStatProps {
  label: string;
  value: number;
  tone: 'emerald' | 'amber' | 'red' | 'orange';
}

function HeadlineStat(props: HeadlineStatProps): JSX.Element {
  const colors: Record<HeadlineStatProps['tone'], string> = {
    emerald: 'text-emerald-700',
    amber:   'text-amber-700',
    red:     'text-red-700',
    orange:  'text-orange-700',
  };
  return (
    <div className="text-sm">
      <span className={`font-semibold tabular-nums ${colors[props.tone]}`}>
        {props.value.toLocaleString()}
      </span>
      <span className="text-gray-500"> {props.label.toLowerCase()}</span>
    </div>
  );
}

export default DryRunSummaryStep;
